import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, inject, Injector, LOCALE_ID, provideAppInitializer, importProvidersFrom } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withHashLocation } from '@angular/router';

import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { provideStore } from '@ngxs/store';
import { provideBsmIconLibrary, provideBsmIcons } from '@bsm-ui-lib/bsm-ui';

import { appBootstrapFactory } from 'app/appBootstrap';
import { RoleResolver } from 'app/resolvers/role.resolver';
import { environment } from '../environments/environment';
import { provideContextBar } from './00_modules/contextbar/provide-contextbar';
import { DEFAULT_TIMEOUT } from './00_modules/core/common/http-timeout.interceptor';
import { provideCore } from './00_modules/core/provide-core';
import { PhotosService } from './00_modules/photos/services/photos.service';

import { APP_ROUTES } from './app.routes';
import { ErrorCatchingInterceptor } from './interceptors/error-catching.interceptor';

import { AppMessageService } from './00_modules/core/services/appMessage.service';
import { NotificationService } from './00_modules/core/services/notification.service';
import { InfoWindowDataService } from './00_modules/master-data/services/info-window-data.service';
import { OKAPI_APP_ICON_DEFINITIONS } from './00_modules/shared/components/fa-icon/fa-icon-library.service';
import { NotificationInterceptor } from './interceptors/notification.interceptor';
import { AppTranslateLoader } from './services/app.translate-loader';
import { HealthService } from './services/health.service';
import { NavigationScrollService } from './services/navigation-scroll.service';
import { UiFacade } from './store/facade/ui.facade';
import { ActiveFormState } from './store/state/active-form.state';
import { AiChatbotState } from './store/state/ai-chatbot.state';
import { ActivityState } from './store/state/activity.state';
import { AppMessageState } from './store/state/app-message.state';
import { AssignmentState } from './store/state/assignment.state';
import { BillingToolState } from './store/state/billing-tool.state';
import { BusinessPartnerState } from './store/state/businessPartner.state';
import { ContextBarState } from './store/state/context-bar.state';
import { CustomerState } from './store/state/customer.state';
import { DashboardState } from './store/state/dashboard.state';
import { DispositionState } from './store/state/disposition.state';
import { DocumentCenterState } from './store/state/document-center.state';
import { DownloadLinkAreaState } from './store/state/download-link-area.state';
import { FieldDefinitionState } from './store/state/field-definition.state';
import { GeolocationSearchState } from './store/state/geolocation-search.state';
import { InvoiceState } from './store/state/invoice.state';
import { MdCustomerState } from './store/state/md-customer.state';
import { NotificationState } from './store/state/notification.state';
import { OrderNoteState } from './store/state/order-note.state';
import { OrderState } from './store/state/ordering.state';
import { ParticipantState } from './store/state/participant.state';
import { PhotoState } from './store/state/photo.state';
import { ProductConfigurationState } from './store/state/product-configuration.state';
import { QuestionnaireState } from './store/state/questionnaire.state';
import { SapSearchState } from './store/state/sap-search.state';
import { TeamSearchState } from './store/state/team-search.state';
import { TextBlockState } from './store/state/text-block.state';
import { GeneralUiState } from './store/state/ui.state';
import { UserState } from './store/state/user.state';
import localeDe from '@angular/common/locales/de';
import { VersionInterceptor } from './00_modules/core/common/version.interceptor';

registerLocaleData(localeDe);

export const appConfig: ApplicationConfig = {
    providers: [
        // NGXS State Management
        provideStore(
            [
                ActiveFormState,
                AiChatbotState,
                ActivityState,
                AppMessageState,
                AssignmentState,
                BillingToolState,
                BusinessPartnerState,
                ContextBarState,
                CustomerState,
                DashboardState,
                DispositionState,
                DocumentCenterState,
                DownloadLinkAreaState,
                FieldDefinitionState,
                GeolocationSearchState,
                InvoiceState,
                MdCustomerState,
                NotificationState,
                OrderNoteState,
                OrderState,
                ParticipantState,
                PhotoState,
                ProductConfigurationState,
                QuestionnaireState,
                SapSearchState,
                TeamSearchState,
                TextBlockState,
                GeneralUiState,
                UserState,
            ],
            {
                developmentMode: !environment.production,
            },
            withNgxsReduxDevtoolsPlugin({
                disabled: environment.production,
            }),
        ),

        // Router
        provideRouter(APP_ROUTES, withHashLocation()),

        // Translation
        importProvidersFrom(
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useClass: AppTranslateLoader,
                },
            }),
        ),

        // HTTP Client with Interceptors
        provideHttpClient(withInterceptorsFromDi()),

        // Animations
        provideAnimations(),

        // Core providers
        provideCore(),
        provideContextBar(),

        // Common services
        Title,

        // Locale
        { provide: LOCALE_ID, useValue: 'de-DE' },

        // App Initializer
        provideAppInitializer(() => {
            const initializerFn = appBootstrapFactory(inject(TranslateService), inject(Injector));
            return initializerFn();
        }),

        provideBsmIcons(OKAPI_APP_ICON_DEFINITIONS),
        provideBsmIconLibrary(),

        // HTTP Interceptors
        {
            provide: HTTP_INTERCEPTORS,
            useClass: NotificationInterceptor,
            multi: true,
            deps: [NotificationService, AppMessageService, UiFacade],
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ErrorCatchingInterceptor,
            multi: true,
            deps: [TranslateService],
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: VersionInterceptor,
            multi: true,
        },

        // Custom providers
        { provide: DEFAULT_TIMEOUT, useValue: 30000 },

        // Standalone Services
        RoleResolver,
        HealthService,
        NavigationScrollService,
        PhotosService,
        InfoWindowDataService,
    ],
};
